import Phaser from "phaser";
import type { MessagePosition, TextSegment } from "@pix/script";
import { MessageRenderer } from "../ui/MessageRenderer";
import { TopBar } from "../ui/TopBar";
import { SaveLoadMenu } from "../ui/SaveLoadMenu";
import { BacklogView } from "../ui/BacklogView";
import { ConfigView } from "../ui/ConfigView";
import type { PlayerState } from "../runtime/playerState";
import type { AudioBackend } from "../audio/audioBackend";
import { loadSave, saveRunner } from "../save/saveStore";
import type { GameScene } from "./GameScene";

type UiState = "idle" | "rendering" | "waiting" | "choice" | "modal";

export class UIScene extends Phaser.Scene {
  private msgBoxBg!: Phaser.GameObjects.Rectangle;
  private nameText!: Phaser.GameObjects.Text;
  private clickHint!: Phaser.GameObjects.Text;
  private msgGroup!: Phaser.GameObjects.Container;
  private choiceContainer!: Phaser.GameObjects.Container;
  private advanceZone!: Phaser.GameObjects.Rectangle;
  private topBar!: TopBar;
  private saveLoad!: SaveLoadMenu;
  private backlog!: BacklogView;
  private config!: ConfigView;

  private state: UiState = "idle";
  private messageResolve: (() => void) | null = null;
  private autoTimer: number | null = null;
  private choiceResolve: ((index: number) => void) | null = null;
  private msgRenderer!: MessageRenderer;

  private playerState!: PlayerState;
  private audio!: AudioBackend;
  private gameScene!: GameScene;
  private gameId!: string;

  constructor() {
    super("UIScene");
  }

  create(): void {
    const w = this.scale.width;
    const h = this.scale.height;

    this.playerState = this.game.registry.get("playerState") as PlayerState;
    this.audio = this.game.registry.get("audio") as AudioBackend;
    this.gameScene = this.game.registry.get("game-scene") as GameScene;
    this.gameId = this.game.registry.get("gameId") as string;

    // メッセージウィンドウ (Phaser Text を 1 文字ずつ並べる方式 — ルビ・色・サイズ対応)
    this.msgBoxBg = this.add
      .rectangle(w / 2, h - 130, w - 80, 220, 0x000000, 0.78)
      .setStrokeStyle(2, 0x4488cc);

    this.nameText = this.add
      .text(70, h - 240, "", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "22px",
        color: "#ffd28a",
        backgroundColor: "#222244",
        padding: { x: 14, y: 6 },
      })
      .setVisible(false);

    this.clickHint = this.add
      .text(w - 90, h - 50, "▼", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "14px",
        color: "#888888",
      })
      .setOrigin(1, 1);

    this.msgRenderer = new MessageRenderer(
      {
        scene: this,
        x: 70,
        y: h - 198,
        maxWidth: w - 160,
        baseSize: 26,
        lineHeight: 40,
        baseColor: "#ffffff",
        fontFamily: "system-ui, sans-serif",
        rubyColor: "#cce6ff",
        topPad: 14,
      },
      (path) => this.resolveVariable(path),
      this.playerState,
    );

    this.msgGroup = this.add
      .container(0, 0, [
        this.msgBoxBg,
        this.nameText,
        this.msgRenderer.getContainer(),
        this.clickHint,
      ])
      .setVisible(false);

    this.choiceContainer = this.add.container(0, 0).setVisible(false);

    // 進行用のクリック領域(TopBar より下)
    this.advanceZone = this.add
      .rectangle(w / 2, (h + 60) / 2, w, h - 60, 0xffffff, 0)
      .setInteractive({ useHandCursor: false });
    this.advanceZone.setDepth(-1);
    this.advanceZone.on("pointerdown", () => this.onAdvanceClick());

    this.saveLoad = new SaveLoadMenu(this, this.gameId);
    this.backlog = new BacklogView(this, this.playerState);
    this.config = new ConfigView(this, this.playerState);

    this.topBar = new TopBar(this, {
      onAuto: () => this.toggleAuto(),
      onSkip: () => this.toggleSkip(),
      onBacklog: () => this.openBacklog(),
      onSave: () => this.openSave(),
      onLoad: () => this.openLoad(),
      onConfig: () => this.openConfig(),
      onTitle: () => this.returnToTitle(),
    });

  }

  // =================================================================
  // Backend 公開メソッド
  // =================================================================

  async showMessage(
    speaker: string | null,
    segments: TextSegment[],
    position?: MessagePosition,
  ): Promise<void> {
    const skipping =
      this.playerState.skipMode &&
      position !== undefined &&
      this.playerState.isRead(position.file, position.cmdIndex);

    if (speaker !== null && speaker !== "") {
      this.nameText.setText(speaker).setVisible(true);
    } else {
      this.nameText.setVisible(false);
    }
    this.msgGroup.setVisible(true);
    this.state = "rendering";

    this.playerState.pushBacklog({
      speaker,
      text: this.msgRenderer.plain(segments),
      timestamp: Date.now(),
    });

    await this.msgRenderer.render(segments);

    if (position) {
      this.playerState.markRead(position.file, position.cmdIndex);
    }

    this.state = "waiting";

    return new Promise((resolve) => {
      this.messageResolve = () => {
        this.messageResolve = null;
        if (this.autoTimer !== null) {
          window.clearTimeout(this.autoTimer);
          this.autoTimer = null;
        }
        this.state = "idle";
        resolve();
      };
      if (skipping) {
        this.autoTimer = window.setTimeout(() => this.messageResolve?.(), 50);
      } else if (this.playerState.autoMode) {
        this.autoTimer = window.setTimeout(
          () => this.messageResolve?.(),
          this.playerState.config.autoDelayMs,
        );
      }
    });
  }

  showChoice(options: { text: TextSegment[]; index: number }[]): Promise<number> {
    this.msgGroup.setVisible(false);
    this.state = "choice";
    this.playerState.autoMode = false;
    this.playerState.skipMode = false;
    this.topBar.setActive("auto", false);
    this.topBar.setActive("skip", false);

    const items = this.choiceContainer.getAll();
    items.forEach((it) => it.destroy());
    this.choiceContainer.removeAll(false);

    const w = this.scale.width;
    const h = this.scale.height;
    const btnH = 60;
    const gap = 12;
    const totalH = options.length * btnH + (options.length - 1) * gap;
    const startY = h / 2 - totalH / 2 + btnH / 2;

    options.forEach((opt, i) => {
      const y = startY + i * (btnH + gap);
      const text = this.msgRenderer.plain(opt.text);

      const bg = this.add
        .rectangle(w / 2, y, Math.min(880, w - 120), btnH, 0x1a2240, 0.92)
        .setStrokeStyle(2, 0x66aaff)
        .setInteractive({ useHandCursor: true });
      const lbl = this.add
        .text(w / 2, y, text, {
          fontFamily: "system-ui, sans-serif",
          fontSize: "22px",
          color: "#ffffff",
        })
        .setOrigin(0.5);

      bg.on("pointerover", () => bg.setFillStyle(0x2a3a78, 1.0));
      bg.on("pointerout", () => bg.setFillStyle(0x1a2240, 0.92));
      bg.on("pointerdown", () => this.selectChoice(opt.index));

      this.choiceContainer.add([bg, lbl]);
    });

    this.choiceContainer.setVisible(true);
    return new Promise((resolve) => {
      this.choiceResolve = resolve;
    });
  }

  // =================================================================
  // 内部イベント
  // =================================================================

  private onAdvanceClick(): void {
    if (this.state === "rendering") {
      this.msgRenderer.fastForward();
    } else if (this.state === "waiting" && this.messageResolve) {
      this.msgGroup.setVisible(false);
      this.messageResolve();
    }
  }

  private selectChoice(index: number): void {
    if (!this.choiceResolve) return;
    const resolve = this.choiceResolve;
    this.choiceResolve = null;
    this.state = "idle";
    this.choiceContainer.setVisible(false);
    const items = this.choiceContainer.getAll();
    items.forEach((it) => it.destroy());
    this.choiceContainer.removeAll(false);
    resolve(index);
  }

  private toggleAuto(): void {
    this.playerState.autoMode = !this.playerState.autoMode;
    if (this.playerState.autoMode) this.playerState.skipMode = false;
    this.topBar.setActive("auto", this.playerState.autoMode);
    this.topBar.setActive("skip", this.playerState.skipMode);
    if (this.state === "waiting" && this.playerState.autoMode && !this.autoTimer) {
      this.autoTimer = window.setTimeout(
        () => this.messageResolve?.(),
        this.playerState.config.autoDelayMs,
      );
    }
  }

  private toggleSkip(): void {
    this.playerState.skipMode = !this.playerState.skipMode;
    if (this.playerState.skipMode) this.playerState.autoMode = false;
    this.topBar.setActive("auto", this.playerState.autoMode);
    this.topBar.setActive("skip", this.playerState.skipMode);
    if (this.state === "rendering") {
      this.msgRenderer.fastForward();
    } else if (this.state === "waiting" && this.playerState.skipMode && !this.autoTimer) {
      this.autoTimer = window.setTimeout(() => this.messageResolve?.(), 50);
    }
  }

  private async openBacklog(): Promise<void> {
    if (this.state === "modal") return;
    const prev = this.state;
    this.state = "modal";
    await this.backlog.open();
    this.state = prev;
  }

  private async openSave(): Promise<void> {
    if (this.state !== "waiting") return;
    const prev = this.state;
    this.state = "modal";
    const result = await this.saveLoad.open("save");
    this.state = prev;
    if (!result.cancel && result.slot) {
      const runner = this.gameScene.getRunner();
      if (runner) saveRunner(runner, this.gameId, result.slot);
    }
  }

  private async openLoad(): Promise<void> {
    if (this.state !== "waiting") return;
    const prev = this.state;
    this.state = "modal";
    const result = await this.saveLoad.open("load");
    if (!result.cancel && result.slot) {
      const runner = this.gameScene.getRunner();
      if (runner) {
        const data = loadSave(this.gameId, result.slot);
        if (data) {
          await runner.setState(data.state);
          this.msgGroup.setVisible(false);
          this.state = "idle";
          this.messageResolve?.();
          return;
        }
      }
    }
    this.state = prev;
  }

  private async openConfig(): Promise<void> {
    if (this.state === "modal") return;
    const prev = this.state;
    this.state = "modal";
    await this.config.open(() => {
      this.audio.setBgmVolume(this.playerState.config.bgmVolume);
      this.audio.setSeVolume(this.playerState.config.seVolume);
      this.audio.setVoiceVolume(this.playerState.config.voiceVolume);
    });
    this.state = prev;
  }

  private returnToTitle(): void {
    const runner = this.gameScene.getRunner();
    runner?.stop();
    this.audio.stopBgm({ fadeout: 0 }).catch(() => {});
    this.scene.stop("GameScene");
    this.scene.stop("UIScene");
    this.scene.start("TitleScene", { gameId: this.gameId });
  }

  private resolveVariable(path: string): unknown {
    const runner = this.gameScene.getRunner();
    if (!runner) return null;
    const segs = path.split(".");
    let cur: unknown = runner.vars.data;
    for (const seg of segs) {
      if (cur === null || cur === undefined || typeof cur !== "object") return null;
      const idxMatch = seg.match(/^([A-Za-z_][A-Za-z0-9_]*)(\[(\d+)\])?$/);
      if (!idxMatch) return null;
      const name = idxMatch[1]!;
      cur = (cur as Record<string, unknown>)[name];
      if (idxMatch[3] !== undefined) {
        if (cur === null || cur === undefined || typeof cur !== "object") return null;
        cur = (cur as Record<string, unknown>)[Number(idxMatch[3])];
      }
    }
    return cur;
  }
}
